import type { Dispatch, SetStateAction } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import { useEffect, useMemo, useState } from 'react'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { GearIcon } from 'src/components/Icons/GearIcon'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'

import { syncContextAddressFromAddressData } from '../../utils/syncContextAddressFromAddressData'
import {
  resolveSelectedAddressId,
  sortDefaultAddressFirst,
} from '../../domain/addressSelection'
import { PostalCodeAddressCard } from './PostalCodeAddressCard'
import { AddressManagerModal } from './AddressManagerModal'
import { ManageAddressesSheetHeader } from './ManageAddressesSheetHeader'

interface PostalCodeAddressListProps {
  defaultAddressId: string | null
  setDefaultAddressId: Dispatch<SetStateAction<string | null>>
  setPostalCode: (postalCode: string) => void
  setAddress: Dispatch<SetStateAction<Address>>
  onEditAddress: (address: AddressData) => void
}

interface AddressListCardRowProps {
  address: AddressData
  orderFormId: string
  setOrderForm: ReturnType<typeof useCheckout>['setOrderForm']
  defaultAddressId: string | null
  selectedAddressId: string | null
  setDefaultAddressId: Dispatch<SetStateAction<string | null>>
  setPostalCode: (postalCode: string) => void
  setAddress: Dispatch<SetStateAction<Address>>
  onSelectAddress: (addressId: string | null) => void
  onEditAddress: (address: AddressData) => void
}

export function PostalCodeAddressList({
  defaultAddressId,
  setDefaultAddressId,
  setPostalCode,
  setAddress,
  onEditAddress,
}: PostalCodeAddressListProps) {
  const { openModal, closeModal } = useModalSheet()
  const { orderForm, setOrderForm } = useCheckout()
  const { postalCode } = useAddressContext()
  const { shippingData, orderFormId, items } = orderForm

  const selectedAddressIdFromOrderForm = resolveSelectedAddressId(
    shippingData,
    postalCode
  )

  const [selectedAddressIdOverride, setSelectedAddressIdOverride] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (
      selectedAddressIdOverride &&
      selectedAddressIdFromOrderForm === selectedAddressIdOverride
    ) {
      setSelectedAddressIdOverride(null)
    }
  }, [selectedAddressIdFromOrderForm, selectedAddressIdOverride])

  const selectedAddressId =
    selectedAddressIdOverride ?? selectedAddressIdFromOrderForm

  const availableAddresses = useMemo(
    () =>
      sortDefaultAddressFirst(
        shippingData?.availableAddresses ?? [],
        defaultAddressId,
        selectedAddressId
      ),
    [shippingData, defaultAddressId, selectedAddressId]
  )

  const previewAddresses = useMemo(
    () => availableAddresses.slice(0, 2),
    [availableAddresses]
  )

  const hasMoreThanTwoAddresses = availableAddresses.length > 2

  const addressesSectionBottomMargin = hasMoreThanTwoAddresses
    ? 'mb-1.5'
    : 'mb-3'

  const openManageAddressesSheet = () => {
    openModal({
      content: (
        <AddressManagerModal
          addresses={availableAddresses}
          orderForm={{ orderFormId, items }}
          setOrderForm={setOrderForm}
          selectedAddressId={selectedAddressId}
          onSelectedAddressChange={setSelectedAddressIdOverride}
          defaultAddressId={defaultAddressId}
          setDefaultAddressId={setDefaultAddressId}
          setPostalCode={setPostalCode}
          setAddress={setAddress}
        />
      ),
      height: '90dvh',
      header: {
        isVisible: true,
        component: <ManageAddressesSheetHeader onBack={closeModal} />,
      },
    })
  }

  if (availableAddresses.length === 0) {
    return null
  }

  return (
    <div>
      <div
        className={`flex items-center justify-between mt-2 ${addressesSectionBottomMargin}`}
      >
        <span className="text-font-size-2 font-semibold uppercase tracking-[0.96px] text-postal-muted">
          Seus endereços
        </span>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded="false"
          onClick={openManageAddressesSheet}
          className="flex gap-1 items-center"
        >
          <GearIcon />
          <span className="text-font-size-2 font-semibold text-restructure-action">
            Gerenciar endereços
          </span>
        </button>
      </div>
      {hasMoreThanTwoAddresses && (
        <div className="flex gap-1 mb-3">
          <span className="text-font-size-2 text-postal-muted">
            Mostrando os mais usados ·
          </span>
          <button
            onClick={openManageAddressesSheet}
            className="text-font-size-2 text-restructure-action font-semibold"
          >
            ver todos os {availableAddresses.length}
          </button>
        </div>
      )}
      <ul
        role="radiogroup"
        aria-label="Endereços salvos"
        className="flex flex-col gap-2.5"
      >
        {previewAddresses.map((address) => (
          <AddressListCardRow
            key={address.addressId}
            address={address}
            orderFormId={orderFormId}
            setOrderForm={setOrderForm}
            defaultAddressId={defaultAddressId}
            selectedAddressId={selectedAddressId}
            setDefaultAddressId={setDefaultAddressId}
            setPostalCode={setPostalCode}
            setAddress={setAddress}
            onSelectAddress={setSelectedAddressIdOverride}
            onEditAddress={onEditAddress}
          />
        ))}
      </ul>
    </div>
  )
}

function AddressListCardRow({
  address,
  orderFormId,
  setOrderForm,
  defaultAddressId,
  selectedAddressId,
  setDefaultAddressId,
  setPostalCode,
  setAddress,
  onSelectAddress,
  onEditAddress,
}: AddressListCardRowProps) {
  const { addressId } = address

  return (
    <PostalCodeAddressCard
      addressData={address}
      orderFormId={orderFormId}
      syncOrderFormFn={setOrderForm}
      isDefault={addressId === defaultAddressId}
      isSelected={addressId === selectedAddressId}
      onMakeDefault={() => setDefaultAddressId(address?.addressId ?? null)}
      onSelect={() => {
        onSelectAddress(address?.addressId ?? null)
        syncContextAddressFromAddressData(address, setAddress, setPostalCode)
      }}
      onEdit={() => onEditAddress(address)}
    />
  )
}
